/**
 * Schema condiviso degli environment.
 *
 * Definisce l'interfaccia `AppEnvironment` (la "shape") e i valori di default
 * condivisi tra tutti i profili. Tutti gli environment file (dev, test, prod,
 * private/*) devono estendere `sharedEnvironment` via spread, in modo che:
 *
 *  1. l'aggiunta di un nuovo campo qui obblighi il TypeScript compiler a
 *     segnalare errore su ogni file che non lo dichiara;
 *  2. i campi opzionali ereditino il default da qui se non sovrascritti.
 *
 * NESSUN segreto o URL cliente in questo file: solo schema e default pubblici.
 */
import type { CompanyUiConfig } from '../app/_models/company-ui-config.model';

export interface AppEnvironment {
	/** Flag Angular standard (abilita enableProdMode). */
	production: boolean;

	/** Etichetta del profilo per debug/diagnostica (es. 'dev', 'test', 'prod', '<client>'). */
	profile: string;

	// --- Backend base URL ---------------------------------------------------
	/** URL base browser-side (usato per fetch da JS). */
	base_url_browser: string;
	/** URL base server-side / reverse proxy. */
	base_url: string;

	// --- Path dei microservizi (relativi a base_url) ------------------------
	portalUrl: string;
	companiesUrl: string;
	wfUrl: string;
	drUrl: string;
	hrurl: string;
	automezziUrl: string;
	bakekaUrl: string;
	onlineshopurl: string;
	bookingUrl: string;
	paymentUrl: string;
	bizAppointmentsUrl: string;
	mailUrl: string;
	/** ws_spring_fatture_config: configurazione degli scheduler che scaricano le fatture */
	fattureConfigUrl: string;
	serviceUrl_Login: string;
	serviceUrl_Documentale: string;
	urlRDBService: string;
	urlRDBSqlServer: string;
	urlStrike: string;
	urlRFIService: string;
	/** `ws_spring_cna`: intake pubblico del form CNA + report della campagna (pagina /campagna-cna). */
	cnaUrl: string;

	// --- Storage / asset ----------------------------------------------------
	imageBaseURL: string;
	/**
	 * Base URL da cui vengono servite le foto prodotto dell'onlineshop.
	 * In DEV vale `/onlineshop-images` ed è servita dal dev server di Angular
	 * (mapping in angular.json → cartella `image_upload_destination_path` del
	 * backend locale). Negli altri profili è opzionale.
	 */
	onlineshopImageUrl?: string;
	/**
	 * Base URL da cui vengono servite le foto degli eventi/bacheca del modulo turismo.
	 * Stesso meccanismo di `onlineshopImageUrl`. Negli altri profili è opzionale.
	 */
	bakekaImageUrl?: string;
	/**
	 * Base URL da cui vengono servite le foto/audio dei luoghi (places) del modulo turismo.
	 * Stesso meccanismo di `onlineshopImageUrl`. Negli altri profili è opzionale.
	 */
	placesImageUrl?: string;
	/**
	 * Base URL della cartella PERSISTENTE turismo servita da LSWS (es.
	 * `https://host/bacheca/turismo`). Se assente viene derivata dall'host di
	 * `imageBaseURL` (`.../assets` → `.../bacheca/turismo`). Da `bakekaImageUrl`/
	 * `placesImageUrl` si ricavano aggiungendo `/bakeka` e `/places`.
	 */
	turismoMediaUrl?: string;

	// --- Provider esterni (chiavi PUBLIC by design per SPA) -----------------
	/** Google Maps JS API key. Restringi via referrer su Google Cloud Console. */
	apiGoogleMapsKey: string;
	/**
	 * Client ID OAuth "applicazione web" per l'accesso con Google. Pubblico per natura:
	 * la verifica del token avviene lato server. Vuoto = pulsante Google nascosto.
	 * Gli origin autorizzati si configurano su Google Cloud Console, progetto datasmart-b63ff.
	 */
	googleClientId?: string;
	/**
	 * SOLO SVILUPPO: OTP che il backend dev genera fisso (`otp.dev.fixed_code` in
	 * ws_spring_portal_login). Se impostato, la pagina di login lo inserisce e verifica da
	 * sola dopo "Invia codice", saltando la schermata OTP. Vuoto/assente = flusso normale.
	 */
	devOtpAutoCode?: string;
	/**
	 * Services ID di Apple per l'accesso web. Apple non ammette localhost e pretende https
	 * su un dominio verificato, quindi il pulsante compare solo sugli host registrati.
	 */
	appleClientId?: string;
	/** PayPal Smart Buttons client ID (pubblico, restringi via dashboard). */
	paypal_clientId: string;
	/**
	 * Chiave di licenza PrimeUI (PrimeNG 22+). Senza chiave valida la libreria mostra
	 * un banner "invalid license" in pagina. Chiave community: uguale per tutte le build.
	 */
	primeui_license: string;
	/** Nexi XPay script URL completo (alias incluso). */
	nexi_script: string;

	// --- Mail folder labels (per IMAP/microservizio mail) -------------------
	cestino: string;
	inarrivo: string;
	inviate: string;
	bozze: string;
	spam: string;

	// --- Misc ---------------------------------------------------------------
	db_prefix: string;
	maxpageelement: string;
	mailmaxpageelement: string;
	id_categoria_contratto: string;
	fg_id_contratto_appalto: string;

	// --- Config UI per azienda (chiave = cmp_id) ----------------------------
	/**
	 * Override UI specifici per azienda, indicizzati per `cmp_id`.
	 * Vuoto = default (tutto visibile). Valorizzato nei preset cliente
	 * (`private/environment.<client>.ts`), MAI in file committati.
	 */
	companyUiConfig: { [companyId: number]: CompanyUiConfig };

	// --- Branding / white-label --------------------------------------------
	/**
	 * Mostra il branding "MyTown" + gli emblemi di finanziamento FESR/UE:
	 *  - logo MyTown e banner loghi UE/Regione Abruzzo nella schermata di login;
	 *  - banner loghi + dicitura di finanziamento nel footer delle pagine.
	 *
	 * `true` di default (build pubblica MyTown). Impostare `false` nelle build
	 * white-label cliente (es. Edilcostruzioni): il login non mostra quei loghi
	 * e il footer riporta solo il copyright.
	 */
	showFundingBranding: boolean;

	/**
	 * Logo mostrato sopra il titolo nella schermata di login delle build
	 * white-label (dove `showFundingBranding` e' false e quindi il logo MyTown
	 * non compare). Path relativo alla root del build, es.
	 * `assets/images/<cliente>_logo.png`. Vuoto = nessun logo.
	 */
	loginLogo?: string;

	/**
	 * Branding per dominio, indicizzato per `window.location.hostname`
	 * (es. `collaudo.edileasy.com`): la stessa build servita da un altro dominio
	 * mostra al login il logo del dominio (vince su `loginLogo`) e, se c'e'
	 * `companyId`, al login entra direttamente in quell'azienda. L'AuthGuard
	 * valida l'accesso: se l'utente non ha l'azienda si apre la prima disponibile.
	 */
	domainBranding?: { [hostname: string]: { loginLogo?: string; companyId?: number } };
}

/**
 * Default condivisi (path relativi dei microservizi, label IMAP, ecc.).
 * Sono uguali in ogni ambiente: cambia solo il `base_url` davanti.
 */
export const sharedEnvironment = {
	// Path microservizi (relativi - vengono prefissati col base_url)
	portalUrl: '/ws_spring_portal_manager/api/',
	companiesUrl: '/ws_spring_cmp/api/',
	wfUrl: '/ws_spring_workflow/api/',
	drUrl: '/ws_spring_data_repository/api/',
	hrurl: '/ws_spring_hr/api/',
	automezziUrl: '/ws_spring_automezzi/api/',
	bakekaUrl: '/ws_spring_bakeka/api/',
	onlineshopurl: '/ws_spring_ecommerce/api/',
	bookingUrl: '/ws_spring_booking/api/',
	paymentUrl: '/ws_spring_payment/api/',
	bizAppointmentsUrl: '/ws_spring_biz_appointments/api/',
	mailUrl: '/ws_spring_mail/api/',
	fattureConfigUrl: '/ws_spring_fatture_config/api/',
	serviceUrl_Login: '/portal_service_ws_spring_login/api/',
	serviceUrl_Documentale: '/ws_spring_documentary/api/',
	urlRDBService: '/rdb-service/rdb_service/',
	urlRDBSqlServer: '/rdb-service/sqlserver_service/',
	urlStrike: '/rdb-service/strike_service/',
	urlRFIService: '/helpdesk_rfi/helpdesk_rfi/',
	cnaUrl: '/ws_spring_cna/api/',

	// Mail folder labels
	cestino: 'Trash@trash@TRASH',
	inarrivo: 'INBOX@inbox@Inbox',
	inviate: 'Sent@SENT@sent',
	bozze: 'Drafts@drafts@DRAFTS',
	spam: '(Max 50mb)',

	// Misc
	db_prefix: 'jdbc:postgresql',
	maxpageelement: '20',
	mailmaxpageelement: '13',
	id_categoria_contratto: '1',
	fg_id_contratto_appalto: '0',

	// Provider keys: SEMPRE vuote nello shared.
	// Vanno valorizzate per-profilo (test/prod) o nel private/environment.*.ts.
	apiGoogleMapsKey: '',
	// Vuoto qui come tutte le altre: il client id vale solo sugli origin autorizzati nella
	// console Google, quindi si valorizza nei profili dei tenant che li hanno (vedi cna)
	googleClientId: '',
	// Vuoto qui di proposito: il Services ID di Apple vale solo per i domini registrati su
	// di esso, quindi si valorizza nei profili dei tenant che li hanno (vedi cna)
	appleClientId: '',
	paypal_clientId: '',
	nexi_script: '',

	// Licenza PrimeUI community (PrimeNG 22+): non e' un segreto, finisce comunque nel bundle.
	primeui_license:
		'eyJpZCI6ImQwMWE4ODBmLWYxNjItNDRiYi04YWVlLThjZTAzMjYyZjBiMSIsInByb2R1Y3QiOiJwcmltZXVpIiwidGllciI6ImNvbW11bml0eSIsInR5cGUiOiJkZXYiLCJpYXQiOjE3ODg3Nzk3NjksImV4cCI6MTgyMDMxNTc2OX0.5_rHCxkPkjBxHLkYTouWC6_DVnmbe2WkNwtN3YVlnl2jThwGehWiCLdzsuhu_MOlTzmqHD59-oJf6LZLXHvEBw',

	// Config UI per azienda: vuoto di default. Override nei preset cliente.
	companyUiConfig: {},

	// Branding finanziamento FESR/UE: attivo di default (build pubblica MyTown).
	showFundingBranding: true,

	// Logo di login per le build white-label: valorizzato nei preset cliente.
	loginLogo: '',
} satisfies Partial<AppEnvironment>;
